import { HttpError, useTranslation, useUpdate } from "@refinedev/core";
import { useForm } from "@refinedev/react-hook-form";
import Input from "components/input";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "components/button";
import toast from "react-hot-toast";
import { REGEX_STRONG_PASSWORD } from "utills/constants";
import { forceLogoutUser } from "routes/wps/attendees/api";
import { getChangePasswordOriginUrl } from "utills/authDomain";

interface IChangePasswordVariables {
  password: string;
  newPassword: string;
  confirmNewPassword: string;
}

export const ChangePassword = () => {
  const { translate } = useTranslation();
  const originUrl = getChangePasswordOriginUrl();

  const ChangePasswordSchema = z
  .object({
    password: z
      .string()
      .min(1, { message: translate("required-password") })
      .min(6, { message: translate("least-six-characters") }),
    newPassword: z
      .string()
      .min(1, { message: translate("new-password-required") })
      .refine(
        (value) => REGEX_STRONG_PASSWORD.test(value ?? ""),
        translate("least-six-characters-include")
      ),
    confirmNewPassword: z
      .string()
      .min(1, { message: translate("confirm-password-required") }),
  })
  .superRefine(({ confirmNewPassword, newPassword }, ctx) => {
    if (confirmNewPassword !== newPassword) {
      ctx.addIssue({
        path: ["confirmNewPassword"],
        code: "custom",
        message: translate("confirm-password-not-match"),
      });
    }
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IChangePasswordVariables, HttpError, IChangePasswordVariables>({
    resolver: zodResolver(ChangePasswordSchema),
  });

  const { mutate: updateMutation, isLoading: updateMutationIsLoading } = useUpdate();

  const onSubmit = (values: IChangePasswordVariables) => {
    console.log("Update password origin url:", originUrl);

    updateMutation(
      {
        resource: "auth",
        id: "password",
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          // domain: "vietnam-mtb-series",
          domain: originUrl,
          password: values.password,
          new_password: values.newPassword,
        },
      },
      {
        onSuccess: () => {
          toast.success(translate("update-success"));

          setTimeout(() => {
            forceLogoutUser();
          }, 2000);
        },
      }
    );
  };

  return (
    <>
      {/* <Head>
        <title>{t("changePasswordPage.title")} | ACTIUP.NET</title>
      </Head> */}
      <div className="w-full h-full flex items-center justify-center">
        <div className="bg-white w-full max-w-md rounded-lg shadow-lg mt-[32px]">
          <div className="divide-y divide-gray-200">
            <h1 className="px-4 py-5 sm:px-6 text-lg font-bold leading-6 uppercase">
              {translate("change-password")}
            </h1>
            <div className="px-4 py-5 sm:px-6">
              <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
                <fieldset
                  disabled={updateMutationIsLoading}
                  className="space-y-4 sm:space-y-6"
                >
                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium leading-6 text-gray-900"
                    >
                      {translate("current-password")}
                    </label>
                    <div className="mt-2">
                      <Input
                        id="password"
                        type="password"
                        error={errors["password"]}
                        {...register("password")}
                      />
                      {errors["password"] ? (
                        <p className="error-text">
                          <span className="font-medium">
                            {errors["password"].message}
                          </span>
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="newPassword"
                      className="block text-sm font-medium leading-6 text-gray-900"
                    >
                      {translate("new-password")}
                    </label>
                    <div className="mt-2">
                      <Input
                        id="newPassword"
                        type="password"
                        error={errors["newPassword"]}
                        {...register("newPassword")}
                      />
                      {errors["newPassword"] ? (
                        <p className="error-text">
                          <span className="font-medium">
                            {errors["newPassword"].message}
                          </span>
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="confirmNewPassword"
                      className="block text-sm font-medium leading-6 text-gray-900"
                    >
                      {translate("confirm-password")}
                    </label>
                    <div className="mt-2">
                      <Input
                        id="confirmNewPassword"
                        type="password"
                        error={errors["confirmNewPassword"]}
                        {...register("confirmNewPassword")}
                      />
                      {errors["confirmNewPassword"] ? (
                        <p className="error-text">
                          <span className="font-medium">
                            {errors["confirmNewPassword"].message}
                          </span>
                        </p>
                      ) : null}
                    </div>
                  </div>
                </fieldset>
                <div>
                  <Button
                    type="submit"
                    className="w-full h-11"
                    loading={updateMutationIsLoading}
                    disabled={updateMutationIsLoading}
                  >
                    {translate("btn-save")}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
